import { AsyncPipe, NgClass } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  inject
} from '@angular/core';
import { LoaderService } from '../../../../services/loader.service';
import { ScreenSizeService, ViewMode } from '../../../../services/screen-size';
import { CommonStateService } from '../../../../services/state/common-state.service';
import { OrtService } from '../../../../services/suchseite/ort.service';
import { PaginationService } from '../../../../services/suchseite/pagination.service';
import { SuchwortService } from '../../../../services/suchseite/suchwort.service';
import { combineLatest, map } from 'rxjs';
import { LIB_CONFIG, LibConfig } from '../../../../lib.config';
import { AccumulatedBildungsangeboteService } from '../../../../services/accumulatedBildungsangebote.service';
import { CorrectionService } from '../../../../services/suchseite/correction.service';
import { RelaxationService } from '../../../../services/suchseite/relaxation.service';
import { AngebotListComponent } from '../../../angebot-list/angebot-list.component';
import { AngebotTermineComponent } from '../../../angebot-termine/angebot-termine.component';
import { SuchergebnisHitsCardComponent } from '../../suchergebnis-hits-card/suchergebnis-hits-card.component';
import { UrlService } from '../../../../services/state/url.service';

@Component({
  selector: 'now-wbsuche-lib-kompakt-ansicht',
  templateUrl: './kompakt-ansicht.component.html',
  styleUrls: ['./kompakt-ansicht.component.scss'],
  imports: [
    SuchergebnisHitsCardComponent,
    NgClass,
    AngebotListComponent,
    AngebotTermineComponent,
    AsyncPipe
  ]
})
export class KompaktAnsichtComponent {
  private readonly suchwortService = inject(SuchwortService);
  private readonly orteService = inject(OrtService);
  private readonly commonStateService = inject(CommonStateService);
  private readonly correctionService = inject(CorrectionService);
  private readonly relaxationService = inject(RelaxationService);
  private readonly screenSizeService = inject(ScreenSizeService);
  paginationService = inject(PaginationService);
  accumulatedBildungsangeboteService = inject(AccumulatedBildungsangeboteService);
  loaderService = inject(LoaderService);
  private readonly urlService = inject(UrlService);
  libConfig = inject<LibConfig>(LIB_CONFIG);

  @Output() terminSelected = new EventEmitter<string>();
  @Input() public loading: boolean = true;

  @ViewChild('termineElement', { read: ElementRef })
  private readonly termineElement?: ElementRef<HTMLElement>;

  public bildungsangebote$ = this.accumulatedBildungsangeboteService.bildungsangebote$;
  public selectedAngebotId$ = this.commonStateService.selectedAngebotId$;
  public correction$ = this.correctionService.correction$;
  public removedToken$ = this.relaxationService.removedToken$;
  public suchwort$ = this.suchwortService.suchwort$;
  public ort$ = this.orteService.ort$;
  public isLargeDesktop$ = this.screenSizeService.rangeFrom$(ViewMode.LARGE_DESKTOP);
  public isDesktop$ = this.screenSizeService.rangeFrom$(ViewMode.DESKTOP);
  public angeboteLoadingByPageChange$ = combineLatest([
    this.loaderService.angeboteTriggeredByPageChange$,
    this.loaderService.bildungsangeboteLoading$
  ]).pipe(
    map(
      ([angeboteTriggeredByPageChange, bildungsangeboteLoading]) =>
        angeboteTriggeredByPageChange && bildungsangeboteLoading
    )
  );
  public angeboteLoadingWithoutPageChange$ = combineLatest([
    this.loaderService.angeboteTriggeredByPageChange$,
    this.loaderService.bildungsangeboteLoading$
  ]).pipe(
    map(
      ([angeboteTriggeredByPageChange, bildungsangeboteLoading]) =>
        !angeboteTriggeredByPageChange && bildungsangeboteLoading
    )
  );
  public headerOffset = 0;

  @HostListener('window:scroll')
  onScroll() {
    if (this.termineElement) {
      // If the element is scrolled to the top ->
      // Adds an offset (height of now header) to the termine element
      // to not be hidden behind the header
      this.headerOffset =
        this.termineElement.nativeElement.getBoundingClientRect().top < 100 ? 100 : 0;
    }
  }

  public setSelectedAngebot(angebotId: number): void {
    this.commonStateService.setSelectedAngebotId(angebotId);
  }

  public prevPageClicked() {
    this.paginationService.previousPage();
    this.loaderService.setAngeboteTriggeredByPageChange();
  }

  public nextPageClicked() {
    this.paginationService.nextPage();
    this.loaderService.setAngeboteTriggeredByPageChange();
  }

  async navigateToThemenfelder() {
    await this.urlService.updateUrl(null, 'themenfelder');
  }
}
